import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm }) => {
  const { t } = useTranslation();
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg w-full max-w-md">
        <div className="flex items-center justify-center mb-4">
          <div className="bg-red-100 dark:bg-red-900 rounded-full p-3">
            <svg
              className="w-6 h-6 text-red-600 dark:text-red-400"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
        </div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 text-center mb-2">
          {t('Delete sector assignment')}
        </h3>
        <p className="text-gray-500 dark:text-gray-400 text-center mb-6">
          {t('Are you sure you want to delete this sector assignment? This action cannot be undone.')}
        </p>
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
          >
            {t('Cancel')}
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            {t('Delete')}
          </button>
        </div>
      </div>
    </div>
  );
};

const SectorSettingsTable = ({ sectors, onEdit, onDelete }) => {
  const { t } = useTranslation();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [sectorToDelete, setSectorToDelete] = useState(null);

  const handleDeleteClick = (sector) => {
    setSectorToDelete(sector);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = () => {
    onDelete(sectorToDelete.id);
    setDeleteModalOpen(false);
    setSectorToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setSectorToDelete(null);
  };

  if (sectors.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="flex justify-center mb-4">
          <svg
            className="w-16 h-16 text-gray-400"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
            />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-1">{t('No sector configured for this project')}</h3>
        <p className="text-gray-500">{t('Start by adding a new sector assignment to a user using the button above.')}</p>
      </div>
    );
  }

  return (
    <>
      <div className="overflow-x-auto rounded-lg relative">
        <table className="min-w-full bg-white dark:bg-gray-800">
          <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-20">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700">
                {t('Username')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700">
                {t('Sector')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700">
                {t('Actions')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {sectors.map((sector) => (
              <tr key={sector.id} className="dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700">
                <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-gray-200">{sector.username}</td>
                <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-gray-200">{sector.sector}</td>
                <td className="px-6 py-4 whitespace-nowrap space-x-2">
                  <button
                    onClick={() => onEdit(sector)}
                    className="bg-yellow-500 text-white px-3 py-1 rounded hover:bg-yellow-600"
                  >
                    {t('Edit')}
                  </button>
                  <button
                    onClick={() => handleDeleteClick(sector)}
                    className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
                  >
                    {t('Delete')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />
    </>
  );
};

export default SectorSettingsTable;